/**
 * The org's analysis queue (plant-analytics K4: the post-import trigger) — typed wrappers over
 * `GET /v1/twin/analysis/jobs` and `POST /v1/twin/analysis/refresh`, plus their hooks. Backend
 * `internal/twin/analysis_jobs.go`; contract `api/openapi.yaml` AnalysisJob.
 *
 * Every import, undo, plant-time change or products merge queues one debounced refresh of the org's
 * analyses (90 s after the latest change, never later than 15 min after the first). The Data sources page
 * shows where that stands, so a person who just imported knows when the analyses have caught up.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";

export interface AnalysisJobStep {
  order: number;
  status: "running" | "done" | "failed";
  started_at: string;
  done_at?: string;
  ms: number;
  error?: string;
  warnings?: string[];
}

export interface AnalysisJob {
  id: string;
  kind: "org_refresh";
  status: "queued" | "running" | "done" | "failed" | "cancelled";
  reasons: string[];
  first_enqueued_at: string;
  not_before: string;
  attempts: number;
  steps: Record<string, AnalysisJobStep>;
  error: string;
  created_at: string;
  claimed_at?: string | null;
  heartbeat_at?: string | null;
  done_at?: string | null;
}

export const analysisJobs = {
  list: (limit = 5) => api.get<{ jobs: AnalysisJob[] }>(`/v1/twin/analysis/jobs?limit=${limit}`),
  refresh: () => api.post<{ job: AnalysisJob; merged: boolean }>("/v1/twin/analysis/refresh", {}),
};

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

export const ANALYSIS_JOBS_KEY = ["twin-analysis-jobs"] as const;

/** The org's newest analysis jobs; polls every 15 s while one is queued or running. */
export function useAnalysisJobsQuery() {
  return useQuery({
    queryKey: ANALYSIS_JOBS_KEY,
    queryFn: () => analysisJobs.list(5),
    enabled: useLiveEnabled(),
    staleTime: 10_000,
    refetchInterval: (q) => {
      const newest = q.state.data?.jobs?.[0];
      return newest && (newest.status === "queued" || newest.status === "running") ? 15_000 : false;
    },
  });
}

/** Ask for a refresh now (org writers). The server merges it into an already-queued job. */
export function useRefreshAnalysis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => analysisJobs.refresh(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ANALYSIS_JOBS_KEY });
    },
  });
}
